import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/**
 * ① 레일 **넘침 화살표** — 정본 `axi-components.html` **H1-5**.
 *
 * 넘치면 목록 위·아래에 화살표가 나타나고, 누르면 **한 칸씩** 움직인다.
 *
 * ── 왜 「더보기」 접기를 대신하나 ──────────────────────────────────────────
 * 옛 `foldRail`(useRailOverflow.ts)은 넘칠 때 **남길 것을 «고른다»** — 지금 보는 화면 →
 * 별로 고정한 메뉴 → 원래 차례 순으로. 그래서 **화면을 옮길 때마다 레일에 있는 메뉴가
 * 달라진다**. 어제 세 번째에 있던 것이 오늘은 「더보기」 안에 있다. 자리를 외워서 누르는
 * 사람에게는 자리가 없어지는 것이다. 화살표는 고르지 않고 **창만 움직이므로** 차례가
 * 절대 안 바뀐다.
 *
 * ── 끝에 닿으면 ────────────────────────────────────────────────────────
 * 그 쪽 화살표는 **안 보인다**. 다만 CSS 가 `visibility:hidden` 으로 **자리는 남긴다** —
 * `display:none` 으로 걷어내면 24px 이 사라져 목록이 통째로 올라갔다 내려온다. 끝에 닿는
 * 일은 스크롤할 때마다 생기므로 그 흔들림이 계속된다.
 *
 * ⚠ 사다리(`--rail-box-h`)를 대신하지 않는다 — **먼저 압축하고, 그래도 넘치면 화살표**다.
 */

/** 한 번 누르면 움직이는 거리(px) — 상자 44 + 항목 간격 4. 「한 화면」은 어디까지 갔는지 모른다. */
export const RAIL_SCROLL_STEP = 48;
/** 길게 누를 때 이어서 움직이는 간격(ms). */
const REPEAT_MS = 110;
/** 길게 누름으로 보는 시간(ms). */
const REPEAT_DELAY = 320;

export interface RailScroll {
  /** 넘쳐서 화살표가 필요한가. 거짓이면 화살표 자리 자체가 없다. */
  overflowing: boolean;
  /** 맨 위인가 — 위 화살표가 안 보인다. */
  atTop: boolean;
  /** 맨 아래인가 — 아래 화살표가 안 보인다. */
  atBottom: boolean;
  /** 한 칸 움직인다. `dir` 은 -1(위) 또는 1(아래). */
  step: (dir: -1 | 1) => void;
  /** 길게 누르기 시작 — `onPointerDown` 에 건다. */
  hold: (dir: -1 | 1) => void;
  /** 길게 누르기 끝 — `onPointerUp`·`onPointerLeave` 에 건다. */
  release: () => void;
}

/**
 * 목록(스크롤 상자)의 넘침 상태를 재고 움직이는 손잡이를 돌려준다.
 *
 * `enabled` 가 거짓이면 언제나 `overflowing: false` 라 호출부의 렌더가 종전과 같아진다.
 * jsdom 처럼 레이아웃이 없는 환경에서는 모든 높이가 0 이라 `overflowing` 이 거짓으로 남는다
 * (기존 NavBar 테스트가 종전과 똑같이 돈다).
 */
export function useRailScroll(listRef: RefObject<HTMLElement | null>, enabled: boolean): RailScroll {
  const [state, setState] = useState({ overflowing: false, atTop: true, atBottom: true });
  const repeat = useRef<ReturnType<typeof setInterval> | null>(null);
  const delay = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sync = useCallback(() => {
    const el = listRef.current;
    if (!el || !enabled) {
      setState((p) => (p.overflowing ? { overflowing: false, atTop: true, atBottom: true } : p));
      return;
    }
    const over = el.scrollHeight - el.clientHeight;
    const next = {
      overflowing: over > 1,
      atTop: el.scrollTop <= 1,
      atBottom: el.scrollTop >= over - 1,
    };
    setState((p) =>
      p.overflowing === next.overflowing && p.atTop === next.atTop && p.atBottom === next.atBottom ? p : next,
    );
  }, [listRef, enabled]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    sync();
    el.addEventListener('scroll', sync, { passive: true });

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(sync);
      ro.observe(el);
      // 항목이 늘거나 줄면 scrollHeight 가 바뀐다.
      for (const child of Array.from(el.children)) {
        if (child instanceof HTMLElement) ro.observe(child);
      }
    }
    window.addEventListener('resize', sync);
    // 웹폰트가 늦게 오면 줄높이가 바뀌어 넘침 여부도 달라진다.
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    fonts?.ready?.then(sync).catch(() => {});

    return () => {
      el.removeEventListener('scroll', sync);
      ro?.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [listRef, sync]);

  const step = useCallback(
    (dir: -1 | 1) => {
      const el = listRef.current;
      if (!el) return;
      el.scrollBy({ top: RAIL_SCROLL_STEP * dir, behavior: 'smooth' });
    },
    [listRef],
  );

  const release = useCallback(() => {
    if (delay.current) { clearTimeout(delay.current); delay.current = null; }
    if (repeat.current) { clearInterval(repeat.current); repeat.current = null; }
  }, []);

  const hold = useCallback(
    (dir: -1 | 1) => {
      release();
      // 메뉴가 스물이면 스무 번 누를 수 없다 — 누르고 있으면 이어서 움직인다.
      delay.current = setTimeout(() => {
        repeat.current = setInterval(() => step(dir), REPEAT_MS);
      }, REPEAT_DELAY);
    },
    [step, release],
  );

  useEffect(() => release, [release]);

  return { ...state, step, hold, release };
}

/**
 * 지금 보고 있는 메뉴가 **창 밖일 때만** 그 자리로 맞춘다.
 *
 * ⚠ 늘 가운데로 끌면 맨 위에서 시작해야 할 때도 목록이 내려가 있어 **위쪽 메뉴가 없어진
 *   것처럼** 보인다. 그래서 「밖일 때만」이다.
 */
export function scrollActiveIntoView(list: HTMLElement | null, activeSelector: string): void {
  if (!list) return;
  const on = list.querySelector<HTMLElement>(activeSelector);
  if (!on) return;
  const top = on.offsetTop;
  const bottom = top + on.offsetHeight;
  if (top >= list.scrollTop && bottom <= list.scrollTop + list.clientHeight) return;
  list.scrollTop = Math.max(0, top - (list.clientHeight - on.offsetHeight) / 2);
}
